import React from 'react';
import {
  Box,
  Button,
  FormLabel,
  Grid,
  LinearProgress,
  Typography
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import UploadFileOutlinedIcon from '@mui/icons-material/UploadFileOutlined';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutlineOutlined';
import axios from 'src/axios';
import { APICONSTANTS } from 'src/components/Constants';
import { nextFreeName } from 'src/util/fileNames';
import AttachmentList, {
  FileName,
  FileTypeIcon,
  attachmentRowSx
} from './AttachmentList';
import {
  ACCEPT_ATTR,
  acceptedSummary,
  describeUploadError,
  validateAttachment
} from './attachments';
import UnsavedChangeNote from './UnsavedChangeNote';
import { unsavedControlSx } from './unsavedChange';
import { useAutoTranslation } from 'src/hooks/useTranslate';

// File attachments on a clinical record form (TGCLOUD-1850).
//
// A drop zone — drag files onto it, or click / press Enter or Space to browse —
// that takes several files at once. Each file gets a row in a queue: refused up
// front if it is not an accepted type or is over the size limit, otherwise
// uploaded ONE AT A TIME with its progress shown, and on failure left on its
// row with the reason and a Retry. Once uploaded it joins the record's list.
//
// Sequential on purpose: each upload's PDF text feeds Suggested Clinical Terms
// (TGCLOUD-1845) through `onUploadComplete`, once per file with that file's
// own response, and one request at a time keeps those analyses and the list
// updates in a simple order.
//
// Props
//   value             the record's fileAttachments (names)
//   onChange(next)    the whole new list. Called once per finished upload and
//                     per removal, possibly several times before the parent has
//                     re-rendered — so parents must merge it with a functional
//                     update (`setFormValues(prev => ({ ...prev, ... }))`),
//                     never spread a captured `formValues`.
//   familyId          the record's familyMemberId; its first 9 characters are
//                     the storage folder. Without it nothing can be uploaded.
//   readOnly          hide the drop zone and Remove
//   onUploadComplete(data, name, file)  once per uploaded file, with the File as it was
//                     uploaded (a Test form also reads a PDF as its lab report, TGCLOUD-1866)
//   onBusyChange(busy)            true while anything is queued or uploading
//   unsaved           the list differs from the record as saved: drawn as the data entry grid
//                     draws an unsaved change (unsavedChange.js), and said for a screen reader
//
// Removing a file only takes it off the list. The stored object is deleted
// after the record is SAVED, by the form (see deleteRemovedAttachments), so
// Remove followed by Cancel loses nothing.

let nextQueueId = 0;

export default function UploadFileList({
  value,
  onChange,
  familyId,
  readOnly,
  onUploadComplete,
  onBusyChange,
  unsaved = false
}) {
  const t = useAutoTranslation();

  const uid = React.useId();
  const inputRef = React.useRef(null);

  // The latest list and callbacks, so a sequence of uploads each builds on the
  // last one's result — not on the `value` that was current when the files
  // were dropped. Reading `value` from the drop's closure is what made the
  // second of two quick uploads replace the first. The ref follows the prop
  // only when the prop CHANGES, so this component's own re-renders (its queue
  // updating) cannot put back a list the parent has not caught up with yet.
  const latestValue = React.useRef(value || []);
  const lastValueProp = React.useRef(value);
  if (value !== lastValueProp.current) {
    lastValueProp.current = value;
    latestValue.current = value || [];
  }
  const onChangeRef = React.useRef(onChange);
  onChangeRef.current = onChange;
  const onUploadCompleteRef = React.useRef(onUploadComplete);
  onUploadCompleteRef.current = onUploadComplete;

  // Every name this record has held while open, so a new file never takes the
  // name of one just removed: that object is still the saved record's until
  // the record is saved, and overwriting it would change the saved record even
  // if the edit is cancelled.
  const seenNames = React.useRef(new Set());
  for (const n of value || []) seenNames.current.add(n);

  const [queue, setQueue] = React.useState([]);
  const [knownLinks, setKnownLinks] = React.useState({});
  const [details, setDetails] = React.useState({});
  const [dragOver, setDragOver] = React.useState(false);
  const dragDepth = React.useRef(0);

  const disabled = !familyId;

  const updateItem = (id, patch) =>
    setQueue((prev) =>
      prev.map((item) => (item.id === id ? { ...item, ...patch } : item))
    );
  const removeItem = (id) =>
    setQueue((prev) => prev.filter((item) => item.id !== id));

  const addFiles = (fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length || readOnly || disabled) return;
    // Names already spoken for: the record's, ones seen this session, and ones
    // queued ahead in this or an earlier drop.
    const taken = new Set([
      ...latestValue.current,
      ...seenNames.current,
      ...queue.filter((q) => q.status !== 'rejected').map((q) => q.name)
    ]);
    const items = files.map((file) => {
      const reason = validateAttachment(file);
      const id = `upload-${(nextQueueId += 1)}`;
      if (reason) {
        return {
          id,
          file,
          name: file.name,
          size: file.size,
          status: 'rejected',
          error: reason
        };
      }
      const name = nextFreeName(file.name, taken);
      taken.add(name);
      return {
        id,
        file,
        name,
        size: file.size,
        status: 'queued',
        progress: 0,
        error: null
      };
    });
    setQueue((prev) => [...prev, ...items]);
  };

  const uploadOne = async (item) => {
    updateItem(item.id, { status: 'uploading', progress: 0, error: null });
    // Renamed to avoid a clash: send the new name, since the server stores the
    // file under the name it receives.
    const toSend =
      item.name === item.file.name
        ? item.file
        : new File([item.file], item.name, {
            type: item.file.type,
            lastModified: item.file.lastModified
          });
    const form = new FormData();
    form.append('fileName', familyId);
    form.append('file_attachment', toSend);
    try {
      const res = await axios.post(APICONSTANTS.fileAttachment, form, {
        onUploadProgress: (e) => {
          if (e?.total)
            updateItem(item.id, {
              progress: Math.round((e.loaded * 100) / e.total)
            });
        }
      });
      const data = res?.data || {};
      const stored = data.storedName || item.name;
      seenNames.current.add(stored);
      if (data.fileURL)
        setKnownLinks((prev) => ({ ...prev, [stored]: data.fileURL }));
      setDetails((prev) => ({
        ...prev,
        [stored]: { size: data.size ?? item.size, uploadedAt: Date.now() }
      }));
      removeItem(item.id);
      const next = [...latestValue.current, stored];
      latestValue.current = next;
      onChangeRef.current?.(next);
      onUploadCompleteRef.current?.(data, stored, toSend);
    } catch (err) {
      const { message, retriable } = describeUploadError(err);
      updateItem(item.id, { status: 'error', error: message, retriable });
    }
  };

  // One at a time: start the next queued file only when none is uploading.
  React.useEffect(() => {
    if (queue.some((item) => item.status === 'uploading')) return;
    const next = queue.find((item) => item.status === 'queued');
    if (next) uploadOne(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queue]);

  const busy = queue.some(
    (item) => item.status === 'queued' || item.status === 'uploading'
  );
  const onBusyChangeRef = React.useRef(onBusyChange);
  onBusyChangeRef.current = onBusyChange;
  React.useEffect(() => {
    onBusyChangeRef.current?.(busy);
  }, [busy]);

  // A file dropped just beside the zone would otherwise be opened by the
  // browser in place of the app, taking the unsaved record with it. While the
  // zone is on screen, a file drop that no drop target has taken is refused
  // (the cursor shows no-drop) instead.
  React.useEffect(() => {
    if (readOnly) return undefined;
    const guard = (e) => {
      if (e.defaultPrevented) return;
      if (!Array.from(e.dataTransfer?.types || []).includes('Files')) return;
      e.preventDefault();
      if (e.type === 'dragover' && e.dataTransfer)
        e.dataTransfer.dropEffect = 'none';
    };
    window.addEventListener('dragover', guard);
    window.addEventListener('drop', guard);
    return () => {
      window.removeEventListener('dragover', guard);
      window.removeEventListener('drop', guard);
    };
  }, [readOnly]);

  const handleRemove = (name) => {
    const next = latestValue.current.filter((n) => n !== name);
    latestValue.current = next;
    onChangeRef.current?.(next);
  };

  const openPicker = () => {
    if (!disabled) inputRef.current?.click();
  };

  // Drag events fire on every child the pointer crosses, so count entries and
  // exits rather than trusting any single dragleave — the naive version
  // flickers, or sticks, as the pointer passes over the icon and text.
  const hasFiles = (e) =>
    Array.from(e.dataTransfer?.types || []).includes('Files');
  const dropHandlers = disabled
    ? {}
    : {
        onDragEnter: (e) => {
          if (!hasFiles(e)) return;
          e.preventDefault();
          dragDepth.current += 1;
          setDragOver(true);
        },
        onDragOver: (e) => {
          if (!hasFiles(e)) return;
          e.preventDefault();
          if (e.dataTransfer) e.dataTransfer.dropEffect = 'copy';
        },
        onDragLeave: () => {
          dragDepth.current = Math.max(0, dragDepth.current - 1);
          if (dragDepth.current === 0) setDragOver(false);
        },
        onDrop: (e) => {
          e.preventDefault();
          dragDepth.current = 0;
          setDragOver(false);
          addFiles(e.dataTransfer?.files);
        }
      };

  const helperId = `${uid}-help`;
  const labelId = `${uid}-label`;

  return (
    <Grid size={12}>
      <Box
        role="group"
        aria-labelledby={labelId}
        data-testid="attachments"
        sx={unsavedControlSx(unsaved)}
      >
        <Box sx={{ pb: 1 }}>
          <FormLabel
            id={labelId}
            className="form-label"
            sx={{ fontWeight: 'bold', color: 'text.primary' }}
          >
            {t('File attachments')}
          </FormLabel>
          {unsaved && <UnsavedChangeNote />}
        </Box>

        {!readOnly && (
          <>
            <Box
              role="button"
              tabIndex={disabled ? -1 : 0}
              aria-disabled={disabled || undefined}
              aria-describedby={helperId}
              data-testid="attachment-drop-zone"
              onClick={openPicker}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  openPicker();
                }
              }}
              {...dropHandlers}
              sx={(theme) => ({
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 1.5,
                px: 2,
                py: 1.75,
                border: '2px dashed',
                borderColor: dragOver ? 'secondary.main' : 'divider',
                borderRadius: 2,
                bgcolor: dragOver
                  ? alpha(theme.palette.secondary.main, 0.08)
                  : 'background.default',
                cursor: disabled ? 'not-allowed' : 'pointer',
                opacity: disabled ? 0.6 : 1,
                transition: 'border-color .15s, background-color .15s',
                outline: 'none',
                '&:hover': disabled ? {} : { borderColor: 'secondary.main' },
                '&:focus-visible': {
                  borderColor: 'secondary.main',
                  boxShadow: `0 0 0 3px ${alpha(
                    theme.palette.secondary.main,
                    0.35
                  )}`
                }
              })}
            >
              <UploadFileOutlinedIcon
                sx={{
                  fontSize: 32,
                  color: dragOver ? 'secondary.main' : 'action.active'
                }}
              />
              <Box sx={{ minWidth: 0 }}>
                <Typography
                  variant="body2"
                  sx={{ fontWeight: 500, color: 'text.primary' }}
                >
                  {dragOver ? (
                    t('Drop to attach')
                  ) : (
                    <>
                      {t('Drag files here or')}{' '}
                      <Box
                        component="span"
                        sx={{
                          color: 'secondary.main',
                          textDecoration: 'underline'
                        }}
                      >
                        {t('browse')}
                      </Box>
                    </>
                  )}
                </Typography>
                <Typography
                  id={helperId}
                  variant="caption"
                  sx={{ color: 'text.secondary', display: 'block' }}
                >
                  {disabled
                    ? t(
                        'Files can be attached once this record belongs to a person.'
                      )
                    : acceptedSummary()}
                </Typography>
              </Box>
            </Box>
            <input
              ref={inputRef}
              type="file"
              multiple
              hidden
              accept={ACCEPT_ATTR}
              data-testid="attachment-input"
              onChange={(e) => {
                addFiles(e.target.files);
                // So picking the same file again still fires a change.
                e.target.value = '';
              }}
            />
          </>
        )}

        {queue.length > 0 && (
          <Box
            component="ul"
            aria-label={t('Uploads')}
            sx={{
              listStyle: 'none',
              m: 0,
              p: 0,
              pt: 1,
              display: 'flex',
              flexDirection: 'column',
              gap: 0.75
            }}
          >
            {queue.map((item) => (
              <QueueRow
                key={item.id}
                item={item}
                onRetry={() =>
                  updateItem(item.id, {
                    status: 'queued',
                    progress: 0,
                    error: null
                  })
                }
                onDismiss={() => removeItem(item.id)}
              />
            ))}
          </Box>
        )}

        <Box sx={{ pt: (value || []).length ? 1 : 0 }}>
          <AttachmentList
            names={value}
            familyId={familyId}
            knownLinks={knownLinks}
            details={details}
            readOnly={readOnly}
            onRemove={handleRemove}
            emptyText={readOnly ? t('No files attached.') : null}
          />
        </Box>
      </Box>
    </Grid>
  );
}

function QueueRow({ item, onRetry, onDismiss }) {
  const t = useAutoTranslation();

  const failed = item.status === 'error' || item.status === 'rejected';
  const label =
    item.status === 'uploading'
      ? t('Uploading {{progress}}%', { progress: item.progress || 0 })
      : item.status === 'queued'
      ? t('Waiting')
      : null;
  return (
    <Box
      component="li"
      data-testid="upload-row"
      data-status={item.status}
      sx={{
        ...attachmentRowSx(false),
        flexWrap: 'wrap',
        rowGap: 0.5,
        py: 0.75,
        borderColor: failed ? 'error.main' : 'divider',
        '&:hover': {}
      }}
    >
      {failed ? (
        <ErrorOutlineIcon fontSize="small" sx={{ color: 'error.main' }} />
      ) : (
        <FileTypeIcon name={item.name} />
      )}
      <Typography
        component="span"
        variant="body2"
        sx={{ display: 'flex', flex: 1, minWidth: 0 }}
      >
        <FileName name={item.name} />
      </Typography>
      {label && (
        <Typography
          variant="caption"
          sx={{ color: 'text.secondary', flexShrink: 0 }}
        >
          {label}
        </Typography>
      )}
      {failed && item.status === 'error' && item.retriable && (
        <Button
          size="small"
          color="secondary"
          onClick={onRetry}
          aria-label={t('Retry {{name}}', { name: item.name })}
          sx={{ flexShrink: 0, minWidth: 0 }}
        >
          {t('Retry')}
        </Button>
      )}
      {item.status !== 'uploading' && (
        <Button
          size="small"
          color="inherit"
          onClick={onDismiss}
          aria-label={`${failed ? 'Dismiss' : 'Cancel'} ${item.name}`}
          sx={{ flexShrink: 0, minWidth: 0, color: 'text.secondary' }}
        >
          {failed ? t('Dismiss') : t('Cancel')}
        </Button>
      )}
      {item.status === 'uploading' && (
        <LinearProgress
          variant="determinate"
          value={item.progress || 0}
          color="secondary"
          aria-label={t('Uploading {{name}}', { name: item.name })}
          sx={{ flexBasis: '100%', height: 4, borderRadius: 2 }}
        />
      )}
      {failed && (
        <Typography
          variant="caption"
          role="alert"
          sx={{ flexBasis: '100%', color: 'error.main', pl: 3.5 }}
        >
          {item.error}
        </Typography>
      )}
    </Box>
  );
}
